import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  padding: 60px 20px;
  background: transparent;
  min-height: 80vh;

  /* Dotted Background requested */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: radial-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px);
    background-size: 30px 30px;
    z-index: -1;
    pointer-events: none;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const Header = styled.div`
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h2`
  font-size: 42px;
  font-weight: 850;
  color: #FFFFFF;
  letter-spacing: -0.03em;
  margin: 0;
  text-transform: uppercase;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const SubText = styled.p`
  font-size: 13px;
  color: #854CE6;
  font-family: 'JetBrains Mono', monospace;
  text-transform: uppercase;
  letter-spacing: 0.25em;
  font-weight: 600;
`;

const SkillsSection = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const CategoryCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(133, 76, 230, 0.3);
  }
`;

const CategoryInfo = styled.div`
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const CategoryName = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #FFFFFF;
  letter-spacing: -0.01em;
  margin: 0;
`;

const SkillList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SkillNode = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  transition: all 0.2s ease;
  cursor: default;

  &::before {
    content: "";
    width: 6px;
    height: 1px;
    background: #854CE6;
    transition: all 0.2s ease;
  }

  &:hover {
    color: #FFFFFF;
    transform: translateX(4px);
    &::before {
      width: 12px;
      background: #FFFFFF;
    }
  }
`;

const coreSkills = [
  {
    title: 'Product Strategy',
    tags: ['Product Visioning', 'Roadmapping', 'Feature Prioritization', 'PRD Writing', 'Market Research', 'Competitive Analysis', 'GTM Planning', 'OKR Strategy']
  },
  {
    title: 'User & Growth',
    tags: ['User Research', 'A/B Testing', 'Retention Strategy', 'Funnel Analysis', 'Cohort Analysis', 'Conversion Optimization', 'UX Strategy']
  },
  {
    title: 'Leadership & Ops',
    tags: ['Stakeholder Mgt', 'Executive Comms', 'Cross-Functional', 'Sprint Planning', 'Roadmap Execution', 'Decision Frameworks', 'Team Alignment']
  },
  {
    title: 'Data & Insights',
    tags: ['SQL Analytics', 'Dashboard Building', 'Statistical Analysis', 'Hypothesis Testing', 'Data-Driven Decisions', 'Performance Metrics']
  },
  {
    title: 'AI Product Dev',
    tags: ['LLM Design', 'RAG System Design', 'Prompt Engineering', 'AI UX Patterns', 'Responsible AI', 'AI Product Strategy']
  },
  {
    title: 'Beyond the Stack',
    isConnect: true,
    desc: 'I believe in building products that are as human as they are technical. Let’s talk about strategy or your next big idea.',
    link: '#contact'
  }
];

const Skills = () => {
  return (
    <Container id="skills">
      <Wrapper>
        <Header
           as={motion.div}
           initial={{ opacity: 0, y: 15 }}
           whileInView={{ opacity: 1, y: 0 }}
           viewport={{ once: true }}
           transition={{ duration: 0.6 }}
        >
          <SubText>Capabilities / toolkit</SubText>
          <Title>Core Skills</Title>
        </Header>

        <SkillsSection>
          {coreSkills.map((cat, index) => (
            <CategoryCard
              key={index}
              as={motion.div}
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              style={cat.isConnect ? { border: '1px solid rgba(133, 76, 230, 0.4)', background: 'rgba(133, 76, 230, 0.03)' } : {}}
            >
              <CategoryInfo>
                <CategoryName>{cat.title}</CategoryName>
              </CategoryInfo>
              {cat.isConnect ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <p style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.5)', lineHeight: '1.6', margin: 0 }}>
                    {cat.desc}
                  </p>
                  <a href={cat.link} style={{ fontSize: '14px', color: '#854CE6', fontWeight: '700', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Let's Connect →
                  </a>
                </div>
              ) : (
                <SkillList>
                  {cat.tags.map((tag, idx) => (
                    <SkillNode key={idx}>{tag}</SkillNode>
                  ))}
                </SkillList>
              )}
            </CategoryCard>
          ))}
        </SkillsSection>
      </Wrapper>
    </Container>
  );
};

export default Skills;